/* eslint-disable react/prop-types */
import React, { ReactElement, useEffect, useMemo, useState } from "react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  useMantineReactTable,
} from "mantine-react-table";
import { formatAddress, formatAmountWithLabel } from "../../utils/formatter";
import { Utxo } from "../../models/Utxo";
import { Group, Text } from "@mantine/core";
import CopyTextComponent from "../copyText/CopyText.component";
import { useQuery } from "@tanstack/react-query";
import { QueryKey } from "../../models/enums";
import { IndexerService } from "../../services/indexer.service";
import { useParams } from "react-router-dom";
import TableFilterDropdown from "../tableFilterDropdown/TableFilterDropdown";

const statusTypes = [
  { value: "all", label: "All types" },
  { value: "p-chain", label: "Exported from C-Chain" },
  { value: "c-chain", label: "Exported from P-Chain" },
];
export interface PendingTransaction {
  type: "Exported from C-Chain" | "Exported from P-Chain";
  txId: string;
  index: string;
  amount: number;
  threshold: number;
}

export function mergeUtxos(putxo: Utxo[], cutxo: Utxo[]): PendingTransaction[] {
  const mappedPutxo: PendingTransaction[] = putxo.map((tx: Utxo) => {
    return {
      type: "Exported from C-Chain",
      txId: tx.txID,
      index: tx.utxoIndex,
      amount: tx.output.amount,
      threshold: tx.output.threshold,
    };
  });

  const mappedCutxo: PendingTransaction[] = cutxo.map((tx: Utxo) => {
    return {
      type: "Exported from P-Chain",
      txId: tx.txID,
      index: tx.utxoIndex,
      amount: tx.output.amount,
      threshold: tx.output.threshold,
    };
  });

  return [...mappedPutxo, ...mappedCutxo];
}
export default function PendingTab({
  active,
}: {
  active: boolean;
}): ReactElement {
  const [pendingTxs, setPendingTxs] = useState<PendingTransaction[]>([]);
  const { address } = useParams();
  const [selectedType, setSelectedType] = useState<
    "c-chain" | "p-chain" | null
  >(null);

  const { data: putxo, isError: isPError } = useQuery({
    queryKey: [QueryKey.pUtxo, address],
    queryFn: (): Promise<Utxo[]> => IndexerService.getPUtxos(address!),
    enabled: !!address && active,
    staleTime: 10000,
  });

  const { data: cutxo, isError: isCError } = useQuery({
    queryKey: [QueryKey.cUtxo, address],
    queryFn: (): Promise<Utxo[]> => IndexerService.getCUtxos(address!),
    enabled: !!address && active,
    staleTime: 10000,
  });

  useEffect(() => {
    let mergedTxs: PendingTransaction[] = [];
    switch (selectedType) {
      case null:
        mergedTxs = mergeUtxos(putxo ?? [], cutxo ?? []);
        break;
      case "p-chain":
        mergedTxs = mergeUtxos(putxo ?? [], []);
        break;
      case "c-chain":
        mergedTxs = mergeUtxos([], cutxo ?? []);
        break;
      default:
        console.warn("Unexpected UTXO type");
    }

    setPendingTxs(mergedTxs);
  }, [putxo, cutxo, selectedType]);

  //should be memoized or stable
  const columns = useMemo<MRT_ColumnDef<PendingTransaction>[]>(
    () => [
      {
        accessorFn: (row) => {
          return (
            <CopyTextComponent
              primaryColor={false}
              text={formatAddress(row.txId)}
              value={row.txId}
              size={"14px"}
              weight={"500"}
            ></CopyTextComponent>
          );
        },
        header: "Id",
        // size: 100,
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "left",
        },
        enableSorting: false,
      },
      {
        accessorKey: "type",
        header: "Type",
        Header: () => (
          <>
            <Text fw={500}>Type</Text>
            <TableFilterDropdown
              values={statusTypes}
              setSelectedValue={(item) => {
                setSelectedType(item ? (item as "c-chain" | "p-chain") : null);
              }}
              selectedValue={selectedType}
            />
          </>
        ),
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "left",
        },
        enableSorting: false,
      },
      {
        accessorKey: "amount",
        header: "Amount",
        mantineTableHeadCellProps: {
          align: "right",
        },
        mantineTableBodyCellProps: {
          align: "right",
        },
        enableSorting: false,
        Cell: ({ cell }) => (
          <>{formatAmountWithLabel(cell.getValue<number>())}</>
        ),
      },
    ],
    [pendingTxs]
  );

  const table = useMantineReactTable({
    columns: pendingTxs ? columns : [],
    data: pendingTxs || [], //must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    mantineTableProps: {
      style: {
        tableLayout: "fixed",
        fontWeight: 500,
        fontSize: "var(--module-size-14)",
      },
    },
    mantineTableHeadCellProps: {
      style: {
        fontWeight: 500,
        fontSize: 15,
      },
    },
    state: {
      isLoading: !pendingTxs,
    },
    mantinePaperProps: {
      withBorder: false,
      shadow: "none",
    },
    pageCount: 10,
    mantinePaginationProps: {
      showRowsPerPage: false,
      withEdges: false,
    },
    enableColumnActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableFilters: false,
    initialState: {
      density: "xs",
    },
  });

  if (isPError && isCError) {
    return (
      <Group h={100} align={"center"} justify={"center"}>
        <span>Data could not be retrieved</span>
      </Group>
    );
  } else if (pendingTxs.length === 0 && selectedType === null) {
    return (
      <Group h={100} align={"center"} justify={"center"}>
        <span>No pending cross-chain transactions</span>
      </Group>
    );
  } else {
    return <MantineReactTable table={table} />;
  }
}
